import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/** Shared loading, error, and copy dialog for importable share links. */
export function ShareLinkDialog({
  open,
  title,
  description,
  url,
  error,
  onRetry,
  onClose,
}: {
  open: boolean
  title: string
  description: string
  url: string
  error?: string
  onRetry?: () => void
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(copiedTimer.current), [])

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      clearTimeout(copiedTimer.current)
      copiedTimer.current = setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {url ? (
          <div className="flex gap-2">
            <input
              value={url}
              readOnly
              aria-label="Share link"
              className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-xs text-foreground outline-hidden"
              onFocus={(event) => event.target.select()}
            />
            <Button size="sm" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        ) : error ? (
          <div className="flex items-center justify-between gap-3" role="alert">
            <p className="text-xs text-destructive">{error}</p>
            {onRetry && (
              <Button size="sm" variant="outline" onClick={onRetry}>
                Retry
              </Button>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" aria-live="polite">
            Creating secure link…
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
